import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { ModelName, ScheduleType } from 'webapp/enums/enums';

import BoardPopupAutoScheduleSnapshotComponent from 'webapp/components/board/popup-auto-schedule-snapshot';

import type { Board, BoardLoop } from 'webapp/models-custom';
import BoardShare from 'webapp/models-custom/board-share';
import type BoardShareRecord from 'webapp/models/board-share';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export default class ModelManagerBoardSharesService extends ModelManagerForStoreService<
	BoardShareRecord,
	BoardShare
> {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	private _scheduledBoardShareModels: BoardShare[] = [];

	get scheduledSavedRecords(): BoardShareRecord[] {
		return this.savedRecords.filter((item) => item.scheduleType); // are scheduled
	}

	@cached
	get scheduledBoardShareModels(): BoardShare[] {
		this.updateModelCache(this._scheduledBoardShareModels, this.scheduledSavedRecords);
		return this._scheduledBoardShareModels;
	}

	createModelForBoard(board: Board, isOneOff = false) {
		const boardShare = this.createNewModel({
			boardId: board.numericId!,
			scheduleType: isOneOff ? ScheduleType.ONE_OFF : ScheduleType.MONTHLY,
		});
		return boardShare;
	}

	createModelForLoop(loop: BoardLoop, isOneOff = false) {
		const boardShare = this.createNewModel({
			loopId: loop.numericId!,
			scheduleType: isOneOff ? ScheduleType.ONE_OFF : ScheduleType.MONTHLY,
		});
		return boardShare;
	}

	createAndEditForBoard(
		board: Board,
		origin: string,
		isOneOff = false,
		isFromCreateChoicePopupFlow = false,
	) {
		const model = this.createModelForBoard(board, isOneOff);
		this.edit(model, origin, undefined, isFromCreateChoicePopupFlow);
	}

	createAndEditForLoop(
		loop: BoardLoop,
		origin: string,
		isOneOff = false,
		isFromCreateChoicePopupFlow = false,
	) {
		const model = this.createModelForLoop(loop, isOneOff);
		this.edit(model, origin, undefined, isFromCreateChoicePopupFlow);
	}

	createNewAndOpenEdit(trackingOrigin: string) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.mmBoardsService.normalBoardModels.length) {
			this.infolayerService.error('To schedule a snapshot please create a dashboard first.');
			return;
		}

		const boardShare = this.createNewModel();

		if (boardShare) {
			this.edit(boardShare, trackingOrigin);
		}
	}

	edit(
		boardShare: BoardShare,
		trackingOrigin: string,
		onSave?: PopupData.Notification.Edit['onSave'],
		isFromCreateChoicePopupFlow = false,
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.mmNotificationsService.openEditPopup(
			boardShare,
			trackingOrigin,
			isFromCreateChoicePopupFlow,
			{ onSave },
		);
	}

	createDuplicatedModel(model: BoardShare) {
		const data = model.getDataForClone();
		return this.createNewModel(data);
	}

	delete(boardShare: BoardShare) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const infolayer = this.infolayerService.loading('Deleting snapshot...');
			boardShare
				.delete()
				.then(
					() => {
						infolayer.ok('Snapshot deleted!');
					},
					(errorData) => {
						infolayer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to delete the scheduled snapshot "${boardShare.boardObject?.name || boardShare?.loopObject?.name}"?`,
			text: '',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	openAutoScheduleSnapshot(props: PopupData.BoardShare.AutoScheduleSnapshot) {
		const popup = this.popupService.createModal(BoardPopupAutoScheduleSnapshotComponent, {
			popupClass: 'popup-type-auto-schedule-board-snapshot',
		});
		popup.open(props);
		return popup;
	}

	// ------ Requests ------

	_boardSharesRequested = false;
	_boardSharesRequestPromise: Promise<void> | null = null;
	requestBoardShares(): Promise<void> {
		const variablePrefix = 'boardShares';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(this.modelName);
			},
		);
	}

	// ------ For-store ------

	modelName = ModelName.BOARD_SHARE;
	override modelTitle = 'scheduled snapshot';

	createModel(model: BoardShareRecord): BoardShare {
		return new BoardShare({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/board-shares': ModelManagerBoardSharesService;
	}
}
